import React from 'react';
import { useAuth } from '../context/AuthContext';
import { motion } from 'framer-motion';
import { 
  User as UserIcon, 
  Envelope, 
  Crown, 
  Calendar,
  Gear
} from '@phosphor-icons/react';
import { Button } from '../components/ui/button';
import { Link } from 'react-router-dom';

const Profile = () => {
  const { user } = useAuth();

  const tierColors = {
    basic: 'text-[#C0C0C0]',
    premium: 'text-white',
    elite: 'text-[#D4AF37]'
  };

  const tierBorderColors = {
    basic: 'border-[#C0C0C0]/20',
    premium: 'border-white/20',
    elite: 'border-[#D4AF37]/30 gold-glow'
  };

  return (
    <div className="min-h-screen bg-[#050505] py-8" data-testid="profile-page">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-8"
        >
          <h1 className="font-outfit text-3xl sm:text-4xl font-light text-white">
            My <span className="text-[#D4AF37]">Profile</span>
          </h1>
        </motion.div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Profile Card */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="lg:col-span-1"
          >
            <div className={`bg-[#121212] border p-8 text-center ${tierBorderColors[user?.subscription_tier] || 'border-white/5'}`}>
              {user?.profile_image ? (
                <img 
                  src={user.profile_image} 
                  alt={user.name} 
                  className="w-24 h-24 rounded-full mx-auto mb-4 border-2 border-[#D4AF37]/30"
                />
              ) : (
                <div className="w-24 h-24 bg-[#D4AF37]/20 rounded-full mx-auto mb-4 flex items-center justify-center">
                  <UserIcon className="w-12 h-12 text-[#D4AF37]" weight="duotone" />
                </div>
              )}
              <h2 className="font-outfit text-xl text-white mb-1">{user?.name}</h2>
              <p className="text-zinc-400 text-sm mb-4">{user?.email}</p>
              <div className={`inline-flex items-center gap-2 px-4 py-2 ${
                user?.subscription_tier === 'elite' ? 'bg-[#D4AF37]/20' :
                user?.subscription_tier === 'premium' ? 'bg-white/10' :
                'bg-zinc-800'
              }`}>
                <Crown className={`w-4 h-4 ${tierColors[user?.subscription_tier]}`} weight="fill" />
                <span className={`text-sm font-medium capitalize ${tierColors[user?.subscription_tier]}`}>
                  {user?.subscription_tier} Member
                </span>
              </div>
            </div>
          </motion.div>

          {/* Account Details */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="lg:col-span-2"
          >
            <div className="bg-[#121212] border border-white/5 p-6">
              <h3 className="font-outfit text-lg text-white mb-6">Account Details</h3>
              
              <div className="space-y-4">
                <div className="flex items-center gap-4 p-4 bg-black/30 border border-white/5">
                  <UserIcon className="w-5 h-5 text-zinc-500" />
                  <div>
                    <p className="text-xs text-zinc-500 uppercase tracking-wider">Full Name</p>
                    <p className="text-white">{user?.name}</p>
                  </div>
                </div>

                <div className="flex items-center gap-4 p-4 bg-black/30 border border-white/5">
                  <Envelope className="w-5 h-5 text-zinc-500" />
                  <div>
                    <p className="text-xs text-zinc-500 uppercase tracking-wider">Email</p>
                    <p className="text-white">{user?.email}</p>
                  </div>
                </div>

                <div className="flex items-center gap-4 p-4 bg-black/30 border border-white/5">
                  <Crown className="w-5 h-5 text-zinc-500" />
                  <div className="flex-1">
                    <p className="text-xs text-zinc-500 uppercase tracking-wider">Subscription</p>
                    <p className={`capitalize ${tierColors[user?.subscription_tier]}`}>
                      {user?.subscription_tier}
                    </p>
                  </div>
                  {user?.subscription_tier === 'basic' && (
                    <Link to="/pricing">
                      <Button size="sm" className="bg-[#D4AF37] text-black hover:bg-[#E5C158]">
                        Upgrade
                      </Button>
                    </Link>
                  )}
                </div>

                <div className="flex items-center gap-4 p-4 bg-black/30 border border-white/5">
                  <Calendar className="w-5 h-5 text-zinc-500" />
                  <div>
                    <p className="text-xs text-zinc-500 uppercase tracking-wider">Member Since</p>
                    <p className="text-white">
                      {user?.created_at ? new Date(user.created_at).toLocaleDateString('en-US', {
                        year: 'numeric',
                        month: 'long',
                        day: 'numeric'
                      }) : 'N/A'}
                    </p>
                  </div>
                </div>

                <div className="flex items-center gap-4 p-4 bg-black/30 border border-white/5">
                  <Gear className="w-5 h-5 text-zinc-500" />
                  <div>
                    <p className="text-xs text-zinc-500 uppercase tracking-wider">Role</p>
                    <p className={`capitalize ${user?.role === 'admin' ? 'text-[#D4AF37]' : 'text-white'}`}>
                      {user?.role}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </motion.div>
        </div>

        {/* Subscription CTA */}
        {user?.subscription_tier !== 'elite' && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="mt-8 bg-gradient-to-r from-[#D4AF37]/20 to-[#B38F24]/20 border border-[#D4AF37]/30 p-8 text-center"
          >
            <h3 className="font-outfit text-2xl text-white mb-2">
              Unlock Your Full Potential
            </h3>
            <p className="text-zinc-400 mb-6 max-w-xl mx-auto">
              Upgrade to {user?.subscription_tier === 'basic' ? 'Premium or Elite' : 'Elite'} for access to all courses, signals, and exclusive features.
            </p>
            <Link to="/pricing">
              <Button className="bg-[#D4AF37] text-black hover:bg-[#E5C158] px-8 py-6 gold-glow">
                View Subscription Options
              </Button>
            </Link>
          </motion.div>
        )}
      </div>
    </div>
  );
};

export default Profile;
